import { captureException } from '@/utils/sentry';
import { create } from 'zustand';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';

export interface SavedCartItem {
  addedAt: string | null;
  orderItemUUID: string | null;
  quantity: number;
  variantPackUUID: string;
}

export interface SavedCart {
  cartItems: SavedCartItem[];
  // Fulfillment metadata - required for order
  fulfillment: SavedSelectedFulfillment;
  lastSavedAt: string;
  lastUpdatedAt: string;
  orderUUID: string | null;
  // Cart metadata - required for saving cart to backend
  restaurantUUID: string;
  supplierTimezone: string;
  supplierUUID: string;
}

interface CartState {
  /**
   * Array of saved carts. Typically only one cart exists at a time.
   *
   * @example
   * ```typescript
   * const savedCarts = useCartStore((state) => state.savedCarts);
   * ```
   */
  savedCarts: SavedCart[];
}

interface CartActions {
  /**
   * Clear all carts from the store
   *
   * @example
   * ```typescript
   * useCartStore.getState().clearCart();
   * ```
   */
  clearCart: () => void;

  /**
   * Get the current cart, or null if no cart exists
   *
   * @returns {SavedCart | null} The current cart, or null if no cart exists
   *
   * @example
   * ```typescript
   * const cart = useCartStore.getState().getCart();
   * ```
   */
  getCart: () => SavedCart | null;

  resetCart: () => void;

  /**
   * Save the cart, typically called when the backend returns the updated cart
   *
   * @param {SavedCart} cart - The cart to save
   *
   * @example
   * ```typescript
   * useCartStore.getState().setCart(cart);
   * ```
   */
  setCart: (cart: SavedCart) => void;

  /**
   * Update the quantity of a specific item in the cart
   *
   * @param {string} variantPackUUID - The UUID of the variant pack
   * @param {number} quantity - The quantity to update the variant pack to
   *
   * @returns {boolean} false when no cart exists and the update was dropped
   *
   * @example
   * ```typescript
   * useCartStore.getState().updateCartItemQuantity(variantPackUUID, quantity);
   * ```
   */
  updateCartItemQuantity: (
    variantPackUUID: string,
    quantity: number,
  ) => boolean;
}

type CartStore = CartState & CartActions;

/**
 * Selector to get the current cart
 * Use this with Zustands selector pattern to prevent unnecessary re-renders
 *
 * @param state - The cart state
 *
 * @returns The current cart, or null if no cart exists
 *
 * @example
 * ```typescript
 * const cart = useCartStore(
 *   useShallow((state) => selectSalesPortalCart(state)),
 * );
 * ```
 */
export const selectSalesPortalCart = (state: CartState): SavedCart | null => {
  // select first cart for now since we don't currently support multicart
  return state.savedCarts[0] ?? null;
};

/**
 * Selector to get the current cart items
 * Use this with Zustands selector pattern to prevent unnecessary re-renders
 *
 * @param state - The cart state
 *
 * @returns The current cart items
 *
 * @example
 * ```typescript
 * const cartItems = useCartStore(
 *   useShallow((state) => selectSalesPortalCartItems(state)),
 * );
 * ```
 */
export const selectSalesPortalCartItems = (
  state: CartState,
): SavedCartItem[] => {
  // select first cart for now since we don't currently support multicart
  return state.savedCarts[0]?.cartItems ?? [];
};

/**
 * Selector to get a specific cart item
 * Use this with Zustands selector pattern to prevent unnecessary re-renders
 *
 * @param state - The cart state
 * @param variantPackUUID - The UUID of the variant pack
 *
 * @returns The cart item, or null if no cart item exists
 *
 * @example
 * ```typescript
 *  const cartItem = useCartStore(
 *    useShallow(
 *      (state) => selectSalesPortalCartItem(state, variantPackUUID) ?? null,
 *    ),
 *  );
 * ```
 */
export const selectSalesPortalCartItem = (
  state: CartState,
  variantPackUUID: string,
): SavedCartItem | null => {
  return (
    selectSalesPortalCart(state)?.cartItems.find(
      (item) => item.variantPackUUID === variantPackUUID,
    ) ?? null
  );
};

/**
 * Selector to get the order ID of the current cart
 * Use this with Zustands selector pattern to prevent unnecessary re-renders
 *
 * @param state - The cart state
 *
 * @returns The order ID of the current cart, or null if no cart exists
 *
 * @example
 * ```typescript
 * const orderId = useCartStore(
 *   useShallow((state) => selectSalesPortalCartOrderId(state)),
 * );
 * ```
 */
export const selectSalesPortalCartOrderId = (
  state: CartState,
): string | null => {
  return selectSalesPortalCart(state)?.orderUUID ?? null;
};

/**
 * Selector to get cart item UUIDs as an array.
 *
 * @param state - The cart state
 *
 * @returns Array of variant pack UUIDs, or empty array if no cart exists
 *
 * @example
 * ```typescript
 * const cartItemUUIDs = useCartStore(selectSalesPortalCartItemUUIDs);
 * ```
 */
export const selectSalesPortalCartItemUUIDs = (state: CartState): string[] => {
  return (
    state.savedCarts[0]?.cartItems.map((item) => item.variantPackUUID) ?? []
  );
};

/**
 * Cart Store for Sales Portal
 */
export const useCartStore = create<CartStore>((set, get) => ({
  clearCart: () => {
    const state = get();
    // select first cart for now since we don't currently support multicart
    const currentCart = state.savedCarts[0] ?? null;
    if (!currentCart) {
      throw new Error(
        `Cannot clear cart: No cart exists. Initialize the cart first with setCart().`,
      );
    }

    set({
      ...state,
      savedCarts: [
        {
          cartItems: [],
          fulfillment: currentCart.fulfillment,
          lastSavedAt: currentCart.lastSavedAt, // preserve lastSavedAt when clearing
          lastUpdatedAt: new Date().toISOString(),
          orderUUID: null,
          restaurantUUID: currentCart.restaurantUUID,
          supplierTimezone: currentCart.supplierTimezone,
          supplierUUID: currentCart.supplierUUID,
        },
      ],
    });
  },
  getCart: () => {
    // select first cart for now since we don't currently support multicart
    return get().savedCarts[0] ?? null;
  },

  resetCart: () => {
    const state = get();
    set({
      ...state,
      savedCarts: [],
    });
  },

  savedCarts: [],

  setCart: (cart: SavedCart) => {
    const state = get();
    set({
      ...state,
      savedCarts: [cart],
    });
  },

  updateCartItemQuantity: (variantPackUUID: string, quantity: number) => {
    // select first cart for now since we don't currently support multicart
    if (!get().savedCarts[0]) {
      captureException(
        new Error(
          'Dropped a cart quantity update: no cart exists for the active restaurant',
        ),
        { extra: { quantity, variantPackUUID } },
      );
      return false;
    }

    set((state) => {
      const cart = state.savedCarts[0];

      let updatedCart: SavedCart;

      if (quantity <= 0) {
        updatedCart = {
          ...cart,
          cartItems: cart.cartItems.filter(
            (item) => item.variantPackUUID !== variantPackUUID,
          ),
          lastUpdatedAt: new Date().toISOString(),
        };
      } else {
        const cartItem = cart.cartItems.find(
          (item) => item.variantPackUUID === variantPackUUID,
        );

        if (cartItem) {
          updatedCart = {
            ...cart,
            cartItems: cart.cartItems.map((item) =>
              item.variantPackUUID === variantPackUUID
                ? { ...item, quantity }
                : item,
            ),
            lastUpdatedAt: new Date().toISOString(),
          };
        } else {
          updatedCart = {
            ...cart,
            cartItems: [
              ...cart.cartItems,
              {
                addedAt: new Date().toISOString(),
                orderItemUUID: null,
                quantity,
                variantPackUUID,
              },
            ],
            lastUpdatedAt: new Date().toISOString(),
          };
        }
      }

      return {
        ...state,
        savedCarts: [updatedCart],
      };
    });
    return true;
  },
}));
